<template>
  <div>
    <v-dialog v-model="dialogOpen" width="550" @keydown.stop.esc="dialogOpen = false">
      <template v-slot:activator="{ props }">
        <slot name="activator" :props="props"></slot>
      </template>

      <div class="dialog">
        <div class="d-flex mb-6 align-center">
          <v-icon size="large" class="mr-3 mt-3" style="margin-top: 2px;">mds:group_add</v-icon>
          <span class="text-title mr-3 mt-3 text-truncate">Invite members</span>
          <v-spacer/>
          <help-dialog-share-project #default="{ props }">
            <v-btn
              variant="text"
              size="small"
              icon="mdi-help-circle-outline"
              v-bind="props"
              class="mr-2"
              :disabled="isWorkflowDisabled"
            />
          </help-dialog-share-project>
          <v-btn variant="text" color="grey" @click="dialogOpen = false" icon="mdi-close">
          </v-btn>
        </div>

        <div class="mb-6">
          <div class="info-alert mb-4">
            <v-icon class="info-icon">mdi-information-outline</v-icon>
            <span class="info-text">
              Invited people need a seat on your plan, or a subscription of their own, to open the project.
            </span>
          </div>
          <invite-team-members-field v-model="invites" :need-seats="needSeats" />
          <div class="text-subtitle mb-3">Members in '{{ project ? project.name : "" }}' with access</div>
          <base-team-member v-for="member in listTeamMembers" :teamMember="member" :project="project" :key="member.id"/>
        </div>

        <div class="d-flex ga-2">
          <v-spacer/>
          <v-btn variant="outlined" size="large" @click="dialogOpen = false" :disabled="isInviting">
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            size="large"
            @click="requestInvite(inviteAndClose)"
            :disabled="!hasValidInvites || isInviting || !canInvite || classifying"
          >
            Invite {{ invites.length > 0 ? `${invites.length} new members` : '' }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <seat-confirmation-dialog
      :confirmation="confirmation"
      :busy="isInviting"
      @confirm="confirm"
      @cancel="cancel"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import BaseTeamMember from '@/components/BaseTeamMember.vue'
import SeatConfirmationDialog from '@/components/SeatConfirmationDialog.vue'
import HelpDialogShareProject from '@/components/HelpDialogShareProject.vue'
import InviteTeamMembersField from '@/components/InviteTeamMembersField.vue'
import { useInviteTeamMembers } from '@/composables/useInviteTeamMembers'
import { useSeatConfirmation } from '@/composables/useSeatConfirmation'

const props = defineProps({
  openShareDialog: {}
})

const emit = defineEmits(['close'])

const store = useStore()

const dialogOpen = ref(false)
const invites = ref([])

const { inviteTeamMembers, isInviting } = useInviteTeamMembers()
const { classifying, needSeats, canInvite, confirmation, requestInvite, confirm, cancel } = useSeatConfirmation(invites)

const listTeamMembers = computed(() => store.state.teamMembers.teamMembers)
const project = computed(() => store.getters['projectMemberships/activeProject'])
const isWorkflowDisabled = computed(() => store.state.isWorkflowDisabled)

const hasValidInvites = computed(() => invites.value && invites.value.length > 0)

const inviteAndClose = async () => {
  await inviteTeamMembers(invites.value, null, needSeats.value)
  invites.value = []
  dialogOpen.value = false
}

watch(() => props.openShareDialog, (val) => {
  dialogOpen.value = val
  if (val) {
    invites.value = []
  }
})

watch(dialogOpen, (val) => {
  if (!val) {
    emit('close')
  }
})
</script>

<style scoped>
.dialog {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--light-1);
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

.info-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: var(--light-2);
  border-radius: 8px;
}

.info-icon {
  color: #353940;
  font-size: 24px;
  flex-shrink: 0;
}

.info-text {
  color: #353940;
  font-size: 14px;
  line-height: 1.5;
}

[data-theme="dark"] .info-icon,
[data-theme="dark"] .info-text {
  color: var(--text-dark);
}
</style>
